/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */

import { VNode } from "preact";
import { html } from "htm/preact";
import { BlissSymbolInfoType, LayoutInfoType } from "../index.d";
import { BlissSymbol } from "../components/BlissSymbol";
import { changeEncodingContents } from "../state/GlobalData";
import { editMessage } from "../core/MessageEdit";
import { generateGridStyle } from "../utils/GridUtils";
import { announceIfEnabled, speakUnavailable } from "../utils/SpeechUtils";
import { caretComposition, removeModifierFromLabel, replaceAtCaret } from "../utils/SymbolEncodingUtils";

type ActionRemoveModifierPropsType = {
  id: string,
  options: BlissSymbolInfoType & LayoutInfoType
};

export function ActionRemoveModifierCell (props: ActionRemoveModifierPropsType): VNode {
  const {
    columnStart, columnSpan, rowStart, rowSpan, label
  } = props.options;
  const removeModifierComposition = props.options.composition;

  const gridStyles = generateGridStyle(columnStart, columnSpan, rowStart, rowSpan);

  // Disabled state of the remove button depends on if the last symbol in the
  // input field (if any) has a modifier AND if there is more than one symbol in
  // the encoding.
  // Marked unavailable rather than `disabled` so the button keeps its place in the tab
  // order for switch and eye-gaze users.
  // A picture has no symbol to remove a modifier from.
  const caretSymbolComposition = caretComposition(changeEncodingContents.value);
  const { payloads, caretPosition } = changeEncodingContents.value;
  const unavailable = caretSymbolComposition === undefined ||
    (payloads[caretPosition].modifierInfo?.length ?? 0) === 0;
  // Handle the request to remove the last placed modifier.
  const cellClicked = () => {
    if (unavailable || caretSymbolComposition === undefined) { speakUnavailable(label); return; }
    // Get the last symbol in the editing area, and create an initial
    // `newBciAvId` and `newLabel`.
    const { caretPosition, payloads } = changeEncodingContents.value;
    const symbolToEdit = payloads[caretPosition];
    let newComposition = (
      typeof caretSymbolComposition === "number" ?
        [caretSymbolComposition] :
        caretSymbolComposition
    );
    let newLabel = symbolToEdit.label;
    let newBaseLabel = symbolToEdit.baseLabel;
    let newBaseModifierCount = symbolToEdit.baseModifierCount;

    // Check for any modifier to remove -- if the symbol has no modifiers,
    // leave the `newComposition` as is. The last modifier is read rather than popped: the
    // message a cell is handed is never edited in place, so the shortened list is a new array.
    // With no modifiers to drop, `slice` returns the empty or undefined list unchanged.
    const modifierCountBeforeRemoval = symbolToEdit.modifierInfo?.length ?? 0;
    const removeInfo = symbolToEdit.modifierInfo?.[modifierCountBeforeRemoval - 1];
    const newModifierInfo = symbolToEdit.modifierInfo?.slice(0, -1);
    if (removeInfo) {
      // Either the last modifer added was prepended to the beginning or
      // appended to the end. If it was prepended ...
      if (removeInfo.isPrepended) {
        // ... the modifier is the first symbol in the `newComposition`.  Remove it
        // plus the following "/"
        newComposition = newComposition.slice((removeInfo.modifierId as (string|number)[]).length + 1);
      }
      // If the last modifier added was appended to the end ...
      else {
        // ... the modifier is the last symbol in the `newComposition`.  Remove it
        // from the end of the array.  Note: the "-1" is to account for the
        // "/" preceding the modifier's composition.
        newComposition = newComposition.slice(
          0, newComposition.length - (removeInfo.modifierId as (string|number)[]).length - 1
        );
      }
      newLabel = removeModifierFromLabel(newLabel, removeInfo.modifierGloss, removeInfo.isPrepended);
      // If the removed modifier predates `baseLabel`'s snapshot (i.e. it was
      // already folded into `baseLabel`'s text when an indicator was applied),
      // strip it from `baseLabel` too and shrink `baseModifierCount` to match --
      // otherwise a later indicator removal would reapply/resurrect a modifier
      // the user just explicitly removed.
      if (newBaseLabel !== undefined && modifierCountBeforeRemoval <= (newBaseModifierCount ?? 0)) {
        newBaseLabel = removeModifierFromLabel(newBaseLabel, removeInfo.modifierGloss, removeInfo.isPrepended);
        newBaseModifierCount = (newBaseModifierCount ?? 0) - 1;
      }
    }
    const edited = replaceAtCaret(payloads, caretPosition, {
      "label": newLabel,
      "composition": newComposition,
      "userSelectedSymbolId": symbolToEdit.userSelectedSymbolId,
      "modifierInfo": newModifierInfo,
      "indicatorId": symbolToEdit.indicatorId,
      "baseLabel": newBaseLabel,
      "baseModifierCount": newBaseModifierCount,
      // Only the modifier's text goes; the model's text stays in the label.
      "isAiLabel": symbolToEdit.isAiLabel
    });
    editMessage({ payloads: edited, caretPosition: caretPosition });
    announceIfEnabled(newLabel);
  };

  return html`
    <button id="${props.id}" class="btn-command" style="${gridStyles}" onClick=${cellClicked} aria-disabled=${unavailable}>
      <${BlissSymbol}
        composition=${removeModifierComposition}
        label=${label}
        isPresentation=true
      />
    </button>
  `;
}
